
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  TextField,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  Alert
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useSnackbar } from "notistack";
import queryString from "query-string";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  
  // Get the action code from the URL
  const queryParams = queryString.parse(location.search);
  const oobCode = queryParams.oobCode as string;

  useEffect(() => {
    if (!oobCode) {
      setVerifying(false);
      setError("Invalid password reset link. The code is missing.");
      return;
    }

    const auth = getAuth();
    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setVerifying(false);
      })
      .catch((error) => {
        console.error("Error verifying reset code:", error);
        setVerifying(false);
        setError("This password reset link is invalid or has expired. Please request a new one.");
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      return;
    }

    if (newPassword.length < 6) {
      enqueueSnackbar("Password must be at least 6 characters", { variant: "error" });
      return;
    }

    setLoading(true);
    const auth = getAuth();

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
      enqueueSnackbar("Password has been reset successfully!", { variant: "success" });
    } catch (error: any) {
      console.error("Error resetting password:", error);
      enqueueSnackbar(error.message || "Failed to reset password", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "error.main" }}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset Password
          </Typography>
          <Paper sx={{ p: 4, mt: 3, width: "100%", textAlign: "center" }}>
             <Alert severity="error" sx={{ mb: 3 }}>
                {error}
             </Alert>
             <Button
                variant="contained" 
                onClick={() => navigate("/sign-in")}
             >
                Return to Sign In
             </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: "success.main" }}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Password Reset Complete
          </Typography>
          <Paper sx={{ p: 4, mt: 3, width: "100%", textAlign: "center" }}>
             <Typography variant="body1" sx={{ mb: 3 }}>
                Your password has been successfully reset. You can now sign in with your new password.
             </Typography>
             <Button
                fullWidth
                variant="contained" 
                onClick={() => navigate("/sign-in")}
             >
                Sign In
             </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            for {email}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="New Password"
            type="password"
            id="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm New Password"
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPassword;
